/**
 * The edition-aware hrefs, as plain functions.
 *
 * Split out of `edition-link.ts` — which must be `"use client"` for
 * `useEditionSlug` — because the SERVER needs the same builders: the front
 * door (`app/page.tsx`) redirects a visitor onward and the root 404 offers
 * them a way back, and both were walking a reseller's visitor into the house
 * product. A server component cannot call a function exported from a
 * `"use client"` module, and the alternative was a second copy of
 * `?edition=${encodeURIComponent(slug)}` living on the server side. Two
 * definitions of one link is how this program's bugs start. There is one, and
 * both realms import it.
 */
/**
 * The same slug, off a query string the caller ALREADY has in hand.
 *
 * `useEditionSlug` cannot resolve before its effect runs, so the first painted
 * frame carries the house href. On most of the auth surface that window closes
 * while the visitor is still reading — but `reset-password`'s two dead-end
 * screens ("Link not valid", "expired or already used") offer nothing to do
 * except click the one link on them, and that page already holds a
 * `useSearchParams()` for the token. Where a component has the params, reading
 * them during render is both correct and strictly earlier; the hook stays for
 * the components that have no params to read, the error boundary above all.
 */
export function editionFromParams(params: { get(name: string): string | null }): string | null {
  const raw = params.get("edition");
  // A slug is `[a-z0-9-]` by construction; anything else is not one and is
  // dropped rather than pasted into an href.
  return raw && /^[a-z0-9][a-z0-9-]{0,62}$/.test(raw) ? raw : null;
}

/** `/onboarding` for an ordinary visitor, the edition's own door for a
 *  reseller's. */
export function onboardingHref(edition: string | null): string {
  return edition ? `/onboarding?edition=${encodeURIComponent(edition)}` : "/onboarding";
}

/**
 * The sign-in page on its own — the right brand, no return trip attached.
 *
 * Distinct from `loginHref` below, which also carries a `callbackUrl` back into
 * the signup funnel. Every call site of THIS one is already on the auth surface
 * (a link back from the reset form, from the auth error boundary, the hop after
 * a finished password reset), where sending someone into `/onboarding` would be
 * a different bug. What they share is the one thing that matters: a reseller's
 * visitor lands on a page wearing the reseller's brand.
 *
 * Written as an early return rather than a ternary on purpose: the literal
 * `: "/login";` is a teeth mutation target in `loginHref`, and String.replace
 * takes the FIRST match, so a second copy of that exact line in this file would
 * silently point that entry at the wrong function.
 */
export function signInHref(edition: string | null): string {
  if (!edition) return "/login";
  return `/login?edition=${encodeURIComponent(edition)}`;
}

/** Asking for a reset link should not walk the visitor out of the edition. */
export function forgotPasswordHref(edition: string | null): string {
  if (!edition) return "/forgot-password";
  return `/forgot-password?edition=${encodeURIComponent(edition)}`;
}

/**
 * The sign-in page, wearing the right brand and returning to wherever the
 * visitor actually was.
 *
 * `edition=` and `callbackUrl=` are two different jobs and both are needed.
 * `callbackUrl` is the return trip — it decides which door they come BACK to
 * after signing in. `edition` is the screen in between: the auth surface
 * resolves its identity per request, and with nothing on the URL it can only
 * find the host's brand. Shipping only the callback is what made
 * BUG-2026-09-11-the-sign-in-link-on-a-reseller-door-hands-the-visitor-a-house-branded-login-page
 * — the dealer's own door, then our logo, our wordmark and "Go to QOD365", then
 * their door again.
 */
export function loginHref(edition: string | null): string {
  return edition
    ? `/login?edition=${encodeURIComponent(edition)}&callbackUrl=${encodeURIComponent(onboardingHref(edition))}`
    : "/login";
}
